import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import type { Settings } from '@/types';

/**
 * Hook to fetch application settings
 */
export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.getSettings(),
    // Settings rarely change, so cache for longer
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update application settings
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Settings>) => api.updateSettings(data),
    onSuccess: (updatedSettings) => {
      // Update settings in cache
      queryClient.setQueryData(queryKeys.settings, updatedSettings);
    },
    // Optimistic update
    onMutate: async (newSettings) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.settings });

      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData<Settings>(
        queryKeys.settings
      );

      // Optimistically update to the new value
      if (previousSettings) {
        queryClient.setQueryData<Settings>(queryKeys.settings, {
          ...previousSettings,
          ...newSettings,
        });
      }

      return { previousSettings };
    },
    onError: (_err, _newSettings, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKeys.settings, context.previousSettings);
      }
    },
  });
}
